import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "./ThemeProvider";
import { useDialogs } from "../context/DialogContext";
import api from "../services/api";

const EXTRAS_LABELS = {
  piscina: "Piscina",
  garaje: "Garaje",
  terraza: "Terraza",
  ascensor: "Ascensor",
  amueblado: "Amueblado",
  primera_linea: "1ª línea",
  vista_mar: "Vista mar",
  pista_padel: "Pádel",
};

/** Resumen legible de un snapshot FILTROS_INIT para chips. */
export function resumirFiltrosAlerta(filtros = {}) {
  const chips = [];
  const f = filtros || {};
  if (f.portales?.length) chips.push(`Portales: ${f.portales.join(", ")}`);
  if (f.portalesExcl?.length) chips.push(`Sin portales: ${f.portalesExcl.join(", ")}`);
  if (f.tipo_anunciante) chips.push(f.tipo_anunciante);
  if (f.tipo_anuncianteExcl) chips.push(`≠ ${f.tipo_anuncianteExcl}`);
  if (f.operacion) chips.push(f.operacion === "alquiler" ? "Alquiler" : "Venta");
  if (f.operacionExcl) chips.push(`≠ ${f.operacionExcl === "alquiler" ? "Alquiler" : "Venta"}`);
  if (f.tipo_inmueble) chips.push(f.tipo_inmueble);
  if (f.tipo_inmuebleExcl) chips.push(`≠ ${f.tipo_inmuebleExcl}`);
  if (f.pobls?.length) chips.push(f.pobls.join(", "));
  if (f.poblsExcl?.length) chips.push(`Sin: ${f.poblsExcl.join(", ")}`);
  if (f.precio_min || f.precio_max) {
    chips.push(`${f.precio_min || "0"}–${f.precio_max || "∞"} €`);
  }
  if (f.hab_min) chips.push(`${f.hab_min} hab+`);
  if (f.hab_minExcl) chips.push(`≠ ${f.hab_minExcl} hab+`);
  if (f.banos_min) chips.push(`${f.banos_min} baños+`);
  if (f.banos_minExcl) chips.push(`≠ ${f.banos_minExcl} baños+`);
  if (f.m2_min || f.m2_max) chips.push(`${f.m2_min || "0"}–${f.m2_max || "∞"} m²`);
  if (f.parcela_min) chips.push(`Parcela ≥ ${f.parcela_min} m²`);
  if (f.orientacion) chips.push(f.orientacion);
  if (f.orientacionExcl) chips.push(`≠ ${f.orientacionExcl}`);
  for (const [k, label] of Object.entries(EXTRAS_LABELS)) {
    if (f[k]) chips.push(label);
    if (f[`${k}Excl`]) chips.push(`Sin ${label}`);
  }
  if (f.sin_vitalcasa) chips.push("Sin Vitalcasa");
  return chips;
}

function canalesLabel(canales = {}) {
  const parts = [];
  if (canales.whatsapp) parts.push("WhatsApp");
  if (canales.email) parts.push("Email");
  if (canales.interna) parts.push("Interna");
  return parts.join(" · ") || "—";
}

/**
 * @param {{ open: boolean, onClose: () => void, filtrosActuales: object }} props
 */
export default function ModalAlertasMarketplace({ open, onClose, filtrosActuales }) {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const dialogs = useDialogs();

  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [editId, setEditId] = useState(null);
  const [nombre, setNombre] = useState("");
  const [canales, setCanales] = useState({ whatsapp: true, email: false, interna: true });
  const [filtrosForm, setFiltrosForm] = useState(null);
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const { data } = await api.get("/scraping/alertas");
      setLista(data?.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Error al cargar alertas");
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setEditId(null);
    setNombre("");
    setCanales({ whatsapp: true, email: false, interna: true });
    setFiltrosForm(filtrosActuales ? { ...filtrosActuales } : null);
    cargar();
  }, [open, filtrosActuales, cargar]);

  if (!open) return null;

  const chips = resumirFiltrosAlerta(filtrosForm || filtrosActuales || {});

  const resetForm = () => {
    setEditId(null);
    setNombre("");
    setCanales({ whatsapp: true, email: false, interna: true });
    setFiltrosForm(filtrosActuales ? { ...filtrosActuales } : null);
  };

  const empezarEditar = (a) => {
    setEditId(a.id);
    setNombre(a.nombre || "");
    setCanales({
      whatsapp: !!a.canales?.whatsapp,
      email: !!a.canales?.email,
      interna: !!a.canales?.interna,
    });
    setFiltrosForm(a.filtros || {});
  };

  const guardar = async () => {
    if (!nombre.trim()) {
      dialogs.alert("Pon un nombre a la alerta.");
      return;
    }
    if (!canales.whatsapp && !canales.email && !canales.interna) {
      dialogs.alert("Elige al menos un canal de aviso.");
      return;
    }
    if (!chips.length) {
      dialogs.alert("Activa al menos un filtro en MarketPlace (o usa «Usar filtros actuales»).");
      return;
    }
    setGuardando(true);
    setError(null);
    try {
      const body = {
        nombre: nombre.trim(),
        filtros: filtrosForm || filtrosActuales,
        canales,
      };
      if (editId) {
        await api.patch(`/scraping/alertas/${editId}`, body);
      } else {
        await api.post("/scraping/alertas", body);
      }
      resetForm();
      await cargar();
    } catch (e) {
      setError(e.response?.data?.error || e.message || "No se pudo guardar");
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async (a) => {
    const ok = await dialogs.confirm(`¿Eliminar la alerta «${a.nombre}»?`);
    if (!ok) return;
    try {
      await api.delete(`/scraping/alertas/${a.id}`);
      if (editId === a.id) resetForm();
      await cargar();
    } catch (e) {
      dialogs.alert(e.response?.data?.error || e.message);
    }
  };

  const toggleActiva = async (a) => {
    try {
      await api.patch(`/scraping/alertas/${a.id}`, { activa: !a.activa });
      await cargar();
    } catch (e) {
      dialogs.alert(e.response?.data?.error || e.message);
    }
  };

  const overlay = (
    <div
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 9200,
        background: "rgba(0,0,0,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        style={{
          width: "min(560px, 100%)",
          maxHeight: "90vh",
          overflow: "auto",
          borderRadius: 12,
          background: isDark ? "#12151c" : "#fff",
          border: `1px solid ${c.sectionBorder}`,
          boxShadow: "0 20px 50px rgba(0,0,0,0.35)",
          fontFamily: "inherit",
        }}
      >
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 16px",
          borderBottom: `1px solid ${c.sectionBorder}`,
        }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: c.textPrimary }}>
              🔔 Alertas MarketPlace
            </div>
            <div style={{ fontSize: 11, color: c.textMuted, marginTop: 2 }}>
              Aviso en anuncios nuevos y bajadas de precio que cumplan tus filtros
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: "none", background: "transparent", cursor: "pointer",
              fontSize: 18, color: c.textMuted, lineHeight: 1, padding: 4,
            }}
          >✕</button>
        </div>

        <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>
          {error && (
            <div style={{
              fontSize: 12, color: "#fecaca", background: "rgba(220,38,38,0.15)",
              padding: "8px 10px", borderRadius: 8,
            }}>{error}</div>
          )}

          {/* Formulario crear / editar */}
          <div style={{
            padding: 12, borderRadius: 10,
            background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
            border: `1px solid ${c.sectionBorder}`,
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: c.textPrimary, marginBottom: 8 }}>
              {editId ? "Editar alerta" : "Nueva alerta"}
            </div>
            <label style={{ display: "block", fontSize: 11, color: c.textMuted, marginBottom: 4 }}>
              Nombre
            </label>
            <input
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Ej. Particulares Dénia &lt; 250k"
              style={{
                width: "100%", boxSizing: "border-box",
                padding: "8px 10px", borderRadius: 8, fontSize: 13,
                border: `1px solid ${c.sectionBorder}`,
                background: isDark ? "#0a0c12" : "#fff",
                color: c.textPrimary, fontFamily: "inherit",
              }}
            />

            <div style={{ marginTop: 10, fontSize: 11, color: c.textMuted, marginBottom: 4 }}>
              Criterios {editId ? "(de la alerta)" : "(filtros actuales de la página)"}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 8 }}>
              {chips.length ? chips.map((t) => (
                <span key={t} style={{
                  fontSize: 10, padding: "2px 8px", borderRadius: 6,
                  background: isDark ? "rgba(59,130,246,0.2)" : "rgba(37,99,235,0.1)",
                  color: isDark ? "#93c5fd" : "#1d4ed8",
                }}>{t}</span>
              )) : (
                <span style={{ fontSize: 11, color: "#f87171" }}>
                  Sin criterios — ajusta filtros en MarketPlace
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => setFiltrosForm(filtrosActuales ? { ...filtrosActuales } : {})}
              style={{
                fontSize: 11, padding: "4px 8px", borderRadius: 6, cursor: "pointer",
                border: `1px solid ${c.sectionBorder}`,
                background: "transparent", color: c.textPrimary, fontFamily: "inherit",
              }}
            >
              Usar filtros actuales de la página
            </button>

            <div style={{ marginTop: 12, fontSize: 11, color: c.textMuted, marginBottom: 6 }}>
              Avisar por
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              {[
                ["whatsapp", "WhatsApp"],
                ["email", "Email"],
                ["interna", "Comunicación interna"],
              ].map(([key, label]) => (
                <label key={key} style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  fontSize: 12, color: c.textPrimary, cursor: "pointer",
                }}>
                  <input
                    type="checkbox"
                    checked={!!canales[key]}
                    onChange={(e) => setCanales((p) => ({ ...p, [key]: e.target.checked }))}
                  />
                  {label}
                </label>
              ))}
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button
                type="button"
                disabled={guardando}
                onClick={guardar}
                style={{
                  padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 700,
                  border: "none", cursor: guardando ? "wait" : "pointer",
                  background: "#2563eb", color: "#fff", fontFamily: "inherit",
                }}
              >
                {guardando ? "Guardando…" : editId ? "Guardar cambios" : "Crear alerta"}
              </button>
              {editId && (
                <button
                  type="button"
                  onClick={resetForm}
                  style={{
                    padding: "8px 14px", borderRadius: 8, fontSize: 12,
                    border: `1px solid ${c.sectionBorder}`,
                    background: "transparent", color: c.textPrimary,
                    cursor: "pointer", fontFamily: "inherit",
                  }}
                >
                  Cancelar edición
                </button>
              )}
            </div>
          </div>

          {/* Lista */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: c.textPrimary, marginBottom: 8 }}>
              Mis alertas {cargando ? "…" : `(${lista.length})`}
            </div>
            {!cargando && !lista.length && (
              <div style={{ fontSize: 12, color: c.textMuted }}>
                Aún no tienes alertas. Configura filtros y crea la primera.
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {lista.map((a) => {
                const aChips = resumirFiltrosAlerta(a.filtros);
                return (
                  <div
                    key={a.id}
                    style={{
                      padding: 10, borderRadius: 10,
                      border: `1px solid ${c.sectionBorder}`,
                      background: isDark ? "rgba(255,255,255,0.02)" : "#fafbfc",
                      opacity: a.activa ? 1 : 0.55,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: c.textPrimary }}>
                          {a.nombre}
                          {!a.activa && (
                            <span style={{ fontWeight: 500, fontSize: 11, color: c.textMuted }}>
                              {" "}(pausada)
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 10, color: c.textMuted, marginTop: 2 }}>
                          {canalesLabel(a.canales)}
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginTop: 6 }}>
                          {aChips.slice(0, 8).map((t) => (
                            <span key={t} style={{
                              fontSize: 9, padding: "1px 6px", borderRadius: 5,
                              background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
                              color: c.textMuted,
                            }}>{t}</span>
                          ))}
                          {aChips.length > 8 && (
                            <span style={{ fontSize: 9, color: c.textMuted }}>+{aChips.length - 8}</span>
                          )}
                        </div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4, flexShrink: 0 }}>
                        <button type="button" onClick={() => empezarEditar(a)} style={btnSec(c, isDark)}>
                          Editar
                        </button>
                        <button type="button" onClick={() => toggleActiva(a)} style={btnSec(c, isDark)}>
                          {a.activa ? "Pausar" : "Activar"}
                        </button>
                        <button
                          type="button"
                          onClick={() => eliminar(a)}
                          style={{ ...btnSec(c, isDark), color: "#f87171" }}
                        >
                          Eliminar
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}

function btnSec(c, isDark) {
  return {
    fontSize: 10,
    padding: "3px 8px",
    borderRadius: 6,
    cursor: "pointer",
    border: `1px solid ${c.sectionBorder}`,
    background: isDark ? "rgba(255,255,255,0.04)" : "#fff",
    color: c.textPrimary,
    fontFamily: "inherit",
  };
}
